import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowLeft, Trash2, ChevronRight, MessageCircle, User, Camera, Layers, Heart, Crown, Search, FileDown,
} from "lucide-react";
import { API, authHeaders } from "../context/AuthContext";
import { useLang } from "../i18n/LangContext";
import { toast } from "sonner";

const MODE_ICON = { conversation: MessageCircle, profile: User, photos: Camera, full: Layers };

export default function History() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [activeTag, setActiveTag] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reports`, { headers: authHeaders() });
      setItems(r.data);
    } catch {/* */} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await axios.delete(`${API}/reports/${id}`, { headers: authHeaders() });
      toast.success(t("deleted"));
      setItems((prev) => prev.filter((p) => p.id !== id));
    } catch { toast.error("Failed"); }
  };

  const downloadPdf = async (id) => {
    try {
      const res = await axios.get(`${API}/reports/${id}/pdf`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `dating-coach-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Nie udało się pobrać PDF");
    }
  };

  const allTags = useMemo(() => {
    const set = new Set();
    items.forEach(it => (it.tags || []).forEach(tg => set.add(tg)));
    return Array.from(set).slice(0, 24);
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter(it => {
      if (tierFilter !== "all" && it.tier !== tierFilter) return false;
      if (activeTag && !(it.tags || []).includes(activeTag)) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!(
          (it.title || "").toLowerCase().includes(s) ||
          (it.contact_name || "").toLowerCase().includes(s) ||
          (it.tags || []).some(tg => tg.toLowerCase().includes(s))
        )) return false;
      }
      return true;
    });
  }, [items, search, tierFilter, activeTag]);

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
      <button onClick={() => navigate("/dashboard")} data-testid="history-back" className="mb-6 inline-flex items-center gap-2 text-sm text-[#a8adbd] hover:text-white">
        <ArrowLeft className="h-4 w-4" />{t("backToDashboard")}
      </button>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <h1 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">{t("historyTitle")}</h1>
        <p className="mt-2 text-sm text-[#a8adbd]">{filtered.length} / {items.length} {t("totalReports").toLowerCase()}</p>
      </motion.div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#a8adbd]" />
          <input data-testid="history-search" value={search} onChange={e => setSearch(e.target.value)} placeholder={t("searchPlaceholder")}
            className="w-full rounded-full border border-[#222636] bg-[#181b24] py-2 pl-9 pr-4 text-sm text-white outline-none placeholder:text-[#a8adbd]/60 focus:border-[#ff4458]" />
        </div>
        <div className="flex rounded-full border border-[#222636] bg-[#181b24] p-1 text-xs">
          {[
            { k: "all", l: t("filterAll") },
            { k: "standard", l: t("filterStandard") },
            { k: "premium", l: t("filterPremium") },
          ].map(o => (
            <button key={o.k} onClick={() => setTierFilter(o.k)} data-testid={`filter-${o.k}`}
              className={`rounded-full px-3 py-1 font-semibold transition-colors ${tierFilter === o.k ? "bg-gradient-to-r from-[#ff4458] to-[#ff6036] text-white" : "text-[#a8adbd] hover:text-white"}`}>
              {o.l}
            </button>
          ))}
        </div>
      </div>

      {allTags.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-2" data-testid="tag-cloud">
          {activeTag && (
            <button onClick={() => setActiveTag(null)} className="rounded-full bg-[#222636] px-2.5 py-1 text-xs text-[#a8adbd] hover:text-white">
              ✕ clear
            </button>
          )}
          {allTags.map(tg => (
            <button key={tg} onClick={() => setActiveTag(tg === activeTag ? null : tg)} data-testid={`tag-${tg}`}
              className={`rounded-full border px-2.5 py-1 text-xs transition-all ${activeTag === tg ? "border-[#ff4458] bg-[#ff4458]/10 text-white" : "border-[#222636] text-[#a8adbd] hover:border-[#ff4458]/40 hover:text-white"}`}>
              #{tg}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex min-h-[30vh] items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#ff4458] border-t-transparent" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="dc-card flex flex-col items-center p-10 text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#222636]">
            <Heart className="h-6 w-6 text-[#ff4458]" />
          </div>
          <h3 className="font-display text-xl font-semibold text-white">{t("noReports")}</h3>
          <p className="mt-2 text-sm text-[#a8adbd]">{t("startFirst")}</p>
          <Link to="/analyze" data-testid="history-empty-cta"
            className="mt-6 inline-flex items-center gap-2 rounded-full dc-gradient-bg px-5 py-2.5 font-display text-sm font-semibold text-white">
            {t("newAnalysis")}<ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      ) : (
        <div className="space-y-3" data-testid="history-list">
          {filtered.map((r, i) => {
            const Icon = MODE_ICON[r.mode] || Layers;
            const isPremium = r.tier === "premium";
            return (
              <motion.div key={r.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: Math.min(i * 0.03, 0.3) }}
                className={`dc-card group flex items-center gap-4 p-4 transition-colors ${isPremium ? "ring-1 ring-[#fdb022]/30" : "hover:border-[#ff4458]/40"}`} data-testid={`history-item-${r.id}`}>
                <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl ${isPremium ? "bg-[#fdb022]/15 text-[#fdb022]" : "bg-[#222636] text-[#ff4458]"}`}>
                  {isPremium ? <Crown className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                </div>
                <Link to={`/report/${r.id}`} className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="truncate font-display font-semibold text-white">{r.title || r.contact_name || "—"}</div>
                    {isPremium && <span className="rounded-full bg-[#fdb022]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#fdb022]">{t("premiumBadge")}</span>}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-[#a8adbd]">
                    {r.contact_name && <span className="truncate">{r.contact_name}</span>}
                    {r.contact_name && <span>·</span>}
                    <span className="capitalize">{r.mode}</span>
                    <span>·</span>
                    <span className="uppercase">{r.language}</span>
                    <span>·</span>
                    <span>{new Date(r.created_at).toLocaleString()}</span>
                  </div>
                  {(r.tags || []).length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {(r.tags || []).slice(0, 5).map(tg => (
                        <span key={tg} className="rounded-full bg-[#222636] px-2 py-0.5 text-[10px] text-[#a8adbd]">#{tg}</span>
                      ))}
                    </div>
                  )}
                </Link>
                <div className="flex flex-shrink-0 items-center gap-3">
                  <div className="hidden text-right sm:block">
                    <div className="font-display text-2xl font-bold dc-gradient-text">{r.overall_score}</div>
                  </div>
                  <button onClick={() => downloadPdf(r.id)} data-testid={`history-pdf-${r.id}`}
                    className="inline-flex items-center gap-1 rounded-full bg-[#222636] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2a2e40]">
                    <FileDown className="h-3.5 w-3.5" /> PDF
                  </button>
                  <Link to={`/report/${r.id}`} data-testid={`history-view-${r.id}`}
                    className="rounded-full bg-[#222636] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2a2e40]">
                    {t("view")}
                  </Link>
                  <button onClick={() => onDelete(r.id)} data-testid={`history-delete-${r.id}`}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-[#222636] text-[#a8adbd] hover:bg-[#fd267a]/15 hover:text-[#fd267a]">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
